import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import set from 'lodash/set'

export { get, set, pick, omit, isEmpty, isEqual }

/**
 * Removes attributes with undefined values from an object
 * @param {Object} object The object to "clean-up"
 * @returns {Object} The cleaned object
 */
export function removeUndefined(object) {
  return Object.keys(object).reduce((acc, cur) => {
    if (object[cur] !== undefined) {
      acc[cur] = object[cur]
    }
    return acc
  }, {})
}

/**
 * Removes attributes with empty values from an object
 * @param {Object} object The object to "clean-up"
 * @returns {Object} The cleaned object
 */
export function removeEmpty(object) {
  if (Array.isArray(object)) {
    return object.filter(
      item =>
        item !== undefined && item !== null && item !== '' && !isEmpty(item)
    )
  }

  return Object.entries(object).reduce((acc, [key, value]) => {
    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (typeof value === 'object' && isEmpty(value))
    ) {
      return acc
    }
    return { ...acc, [key]: value }
  }, {})
}

/**
 * Simple object check.
 * @param {any} item - object to check
 * @returns {boolean}
 */
export function isObject(item) {
  return Boolean(
    item && typeof item === 'object' && !Array.isArray(item) && item !== null
  )
}

/**
 * Merge source object with target object (custom deep merge).
 * - if values are objects, merge them too.
 * - if target value is undefined, use source value.
 * - if source value is undefined, use target value.
 * - if both values are undefined, use undefined.
 *
 * @param {Object} source - source object
 * @param {Object} target - target object
 * @returns {Object} - merged object
 */
export function merge(source, target) {
  if (!isObject(source) || !isObject(target)) return {}

  const merged = {}

  Object.keys(source).forEach(key => {
    const sourceValue = source[key]
    const targetValue = target[key]

    if (isObject(sourceValue) && isObject(targetValue)) {
      merged[key] = merge(sourceValue, targetValue)
    } else if (targetValue) {
      merged[key] = targetValue
    } else if (sourceValue) {
      merged[key] = sourceValue
    }
  })

  return merged
}

export function deepEqual(obj1, obj2) {
  // Check if both are the same reference or strictly equal
  if (obj1 === obj2) return true

  // Handle null and non-object types
  if (
    obj1 == null ||
    obj2 == null ||
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object'
  ) {
    return obj1 === obj2
  }

  // Check if both are arrays
  const isArray1 = Array.isArray(obj1)
  const isArray2 = Array.isArray(obj2)
  if (isArray1 !== isArray2) return false

  // Get keys and compare lengths for early exit
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)
  if (keys1.length !== keys2.length) return false

  // For arrays, compare elements in order
  if (isArray1) {
    for (let i = 0; i < keys1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false
    }
    return true
  }

  // For objects, check if all keys in obj1 exist in obj2 and values are deeply equal
  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false
    }
  }

  return true
}
